/**
 * DragManager - Centralized drag state management
 * This separates visual drag state from data persistence
 */

import { create } from 'zustand';

export interface DragPoint {
  pointId: string;
  assemblyId: string;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

interface DragState {
  // Drag mode
  isDragging: boolean;
  dragMode: 'single' | 'group' | null;

  // Points being dragged
  draggedPoints: Map<string, DragPoint>;

  // Drag offset from cursor
  dragOffset: { x: number; y: number };
  startCursorPos: { x: number; y: number };

  // Temporary visual state to prevent flicker during server update
  isTransitioning: boolean;
  transitionPoints: Map<string, { x: number; y: number }>;

  // Actions
  startDrag: (
    mode: 'single' | 'group',
    points: DragPoint[],
    cursorPos: { x: number; y: number }
  ) => void;
  updateDrag: (cursorPos: { x: number; y: number }) => void;
  endDrag: () => { pointId: string; finalX: number; finalY: number }[];
  cancelDrag: () => void;
  completeTransition: () => void;

  // Helpers
  getPointPosition: (pointId: string) => { x: number; y: number } | null;
  isDraggingPoint: (pointId: string) => boolean;
  isPointInTransition: (pointId: string) => boolean;
}

export const useDragManager = create<DragState>((set, get) => ({
  isDragging: false,
  dragMode: null,
  draggedPoints: new Map(),
  dragOffset: { x: 0, y: 0 },
  startCursorPos: { x: 0, y: 0 },
  isTransitioning: false,
  transitionPoints: new Map(),

  startDrag: (mode, points, cursorPos) => {
    const draggedPoints = new Map<string, DragPoint>();
    points.forEach(point => {
      draggedPoints.set(point.pointId, {
        ...point,
        currentX: point.startX,
        currentY: point.startY,
      });
    });

    set({
      isDragging: true,
      dragMode: mode,
      draggedPoints,
      startCursorPos: { ...cursorPos },
      dragOffset: { x: 0, y: 0 },
    });

    // Broadcast drag start event for coordinated dragging with annotations
    try {
      document.dispatchEvent(
        new CustomEvent('coordinated:drag-start', {
          detail: {
            mode,
            draggedPoints: Array.from(draggedPoints.values()),
            cursorPos,
          },
        })
      );
    } catch {}
  },

  updateDrag: cursorPos => {
    const state = get();
    if (!state.isDragging) return;

    const offset = {
      x: cursorPos.x - state.startCursorPos.x,
      y: cursorPos.y - state.startCursorPos.y,
    };

    // Only update if the offset has actually changed (reduces unnecessary updates)
    if (
      Math.abs(offset.x - state.dragOffset.x) < 0.5 &&
      Math.abs(offset.y - state.dragOffset.y) < 0.5
    ) {
      return;
    }

    // Update all dragged points positions more efficiently
    const updatedPoints = new Map();
    state.draggedPoints.forEach((point, pointId) => {
      updatedPoints.set(pointId, {
        ...point,
        currentX: point.startX + offset.x,
        currentY: point.startY + offset.y,
      });
    });

    set({
      dragOffset: offset,
      draggedPoints: updatedPoints,
    });

    // Broadcast drag events for coordinated dragging with annotations
    try {
      document.dispatchEvent(
        new CustomEvent('coordinated:drag-update', {
          detail: {
            offset,
            draggedPoints: Array.from(updatedPoints.values()),
            cursorPos,
          },
        })
      );
    } catch {}
  },

  endDrag: () => {
    const state = get();
    if (!state.isDragging) return [];

    // Collect final positions
    const finalPositions: { pointId: string; finalX: number; finalY: number }[] = [];
    const transitionPoints = new Map<string, { x: number; y: number }>();

    state.draggedPoints.forEach(point => {
      finalPositions.push({
        pointId: point.pointId,
        finalX: point.currentX,
        finalY: point.currentY,
      });

      // Store final positions for transition rendering
      transitionPoints.set(point.pointId, {
        x: point.currentX,
        y: point.currentY,
      });
    });

    // Enter transition state - keep visual positions while server updates
    set({
      isDragging: false,
      dragMode: null,
      draggedPoints: new Map(),
      dragOffset: { x: 0, y: 0 },
      isTransitioning: true,
      transitionPoints,
    });

    // Broadcast drag end event for coordinated dragging with annotations
    try {
      document.dispatchEvent(
        new CustomEvent('coordinated:drag-end', {
          detail: {
            finalPositions,
            draggedPoints: Array.from(state.draggedPoints.values()),
          },
        })
      );
    } catch {}

    return finalPositions;
  },

  cancelDrag: () => {
    set({
      isDragging: false,
      dragMode: null,
      draggedPoints: new Map(),
      dragOffset: { x: 0, y: 0 },
      isTransitioning: false,
      transitionPoints: new Map(),
    });
  },

  completeTransition: () => {
    set({
      isTransitioning: false,
      transitionPoints: new Map(),
    });
  },

  getPointPosition: pointId => {
    const state = get();

    // If we're dragging, return the dragged position
    if (state.isDragging) {
      const point = state.draggedPoints.get(pointId);
      if (point) {
        return { x: point.currentX, y: point.currentY };
      }
    }

    // If we're transitioning, return the transition position
    if (state.isTransitioning) {
      const transitionPos = state.transitionPoints.get(pointId);
      if (transitionPos) {
        return transitionPos;
      }
    }

    return null;
  },

  isDraggingPoint: pointId => {
    return get().draggedPoints.has(pointId);
  },

  isPointInTransition: pointId => {
    return get().transitionPoints.has(pointId);
  },
}));
